import { _ } from "js/vendor";
import * as geom from "js/core/utilities/geom";
import { BaseElement } from "js/elements/base/BaseElement";
import { CollectionElement, CollectionItemElement } from "js/elements/base/CollectionElement";
import { TextElement } from "js/elements/base/TextElement";
import { AnimationSequenceType, FormatType, ContentBlockType } from "common/constants";
import { Icon } from "js/elements/base/IconElement";
import { formatter } from "js/core/utilities/formatter";
import React from "react";
import { getSVGStyleProps, getTransformProps, SVGGroup } from "../../core/utilities/svgHelpers";

class PictorialCharts extends CollectionElement {
    getChildItemType() {
        return PictorialChartItem;
    }

    get defaultItemData() {
        let defaultShape = "male";
        if (this.model.items.length > 0) {
            defaultShape = _.last(this.model.items).shape;
        }
        return {
            value: 75,
            shape: defaultShape,
            description: ""
        };
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let layouter = this.getLayouter(this.itemElements, size);
        layouter.calcColumnLayout();
        layouter.alignHorizontally();
        layouter.alignVertically();

        props.isFit = layouter.isFit;

        return { size };
    }
}

class PictorialChartItemLabel extends TextElement {
    get canEdit() {
        return false;
    }
}

class PictorialChartItemIcons extends BaseElement {
    // TODO: figure out why powerpoint is not rendering svg correctly
    get exportAsImage() {
        return true;
    }

    get iconId() {
        return this.model.iconId || "figure-man";
    }

    get currentValue() {
        if (this.canvas.isAnimating) {
            return this.model.value * this.animationState.value;
        }

        return this.model.value;
    }

    _build() {
        this.icon = this.addElement("icon", () => Icon, {
            icon: this.iconId,
            preventExport: true,
            preventRender: true
        });
    }

    _calcProps(props, options) {
        const { size, children } = props;

        // calculate the icon props
        const iconProps = children.setProps(this.icon, new geom.Size(1000, size.height)); // width doesn't matter because we are just getting aspect ratio to fit to the height

        const iconHeight = size.height;
        const iconWidth = iconHeight * iconProps.size.aspectRatio;

        const minGap = 10;
        const iconCount = Math.floor(size.width / (iconWidth + minGap));
        const iconGap = (size.width - (iconWidth * iconCount)) / iconCount;

        const iconsBounds = [];
        let x = 0;
        for (let i = 0; i < iconCount; i++) {
            iconsBounds.push(new geom.Rect(x, 0, iconWidth, iconHeight));
            x += iconWidth + iconGap;
        }

        const maskBounds = new geom.Rect(0, 0, size.width * (this.currentValue / 100), iconHeight);

        return { size, maskBounds, iconsBounds };
    }

    renderChildren(props, transition) {
        const { styles, iconsBounds, maskBounds } = props;

        return (
            <SVGGroup key={this.id}>
                <g className="backgroundIcons" {...getSVGStyleProps(styles.unhilited_icons)}>
                    {iconsBounds.map((bounds, idx) => {
                        return <g key={idx} {...getTransformProps(bounds)}>{this.icon.renderChildren(this.icon)}</g>;
                    })}
                </g>
                <clipPath id={`${this.uniqueId}-clip-path`}>
                    <rect {...getTransformProps(maskBounds)} />
                </clipPath>
                <g className="foregroundIcons" clipPath={`url(#${this.uniqueId}-clip-path`} {...getSVGStyleProps(styles.hilited_icons)}>
                    {iconsBounds.map((bounds, idx) => {
                        return <g key={idx} {...getTransformProps(bounds)}>{this.icon.renderChildren(this.icon)}</g>;
                    })}
                </g>
            </SVGGroup>
        );
    }
}

class PictorialChartItem extends CollectionItemElement {
    get calculateChangedProps() {
        return true;
    }

    get format() {
        return this.model.format || FormatType.PERCENT;
    }

    get formatOptions() {
        return this.model.formatOptions || formatter.getDefaultFormatOptions();
    }

    get currentValue() {
        if (this.canvas.isAnimating) {
            return this.model.value * this.animationState.value;
        }

        return this.model.value;
    }

    formatValue(value) {
        let displayValue = value;
        if (this.format == FormatType.PERCENT) {
            displayValue = value / 100;
        } else {
            displayValue = value;
        }

        return formatter.formatValue(displayValue, this.format, this.formatOptions);
    }

    get showDescription() {
        return !_.isEmpty(this.model.description) || this.showDescriptionPlaceholder == true;
    }

    get rolloverPadding() {
        return 0;
    }

    get labelText() {
        return this.formatValue(this.currentValue);
    }

    _build() {
        if (this.model.value == undefined) {
            this.model = {
                value: 75,
                iconId: "figure-man",
                description: ""
            };
        }

        this.icons = this.addElement("icons", () => PictorialChartItemIcons);

        this.label = this.addElement("label", () => PictorialChartItemLabel, {
            model: {
                label: this.labelText
            },
            autoWidth: true,
            autoHeight: true
        });

        if (this.showDescription) {
            this.description = this.addElement("description", () => TextElement, {
                rollover: "PictorialChartDescriptionRollover",
                selection: "PictorialChartDescriptionSelection",
                autoHeight: true,
                allowedBlockTypes: [ContentBlockType.HEADING, ContentBlockType.TITLE, ContentBlockType.BODY, ContentBlockType.DIVIDER, ContentBlockType.CAPTION]
            });
        }
    }

    _calcProps(props, options) {
        const { size, children } = props;

        if (this.canvas.isAnimating) {
            this.label.updateText(this.labelText);
        }

        const labelProps = children.setProps(this.label, size);
        if (this.canvas.isAnimating) {
            labelProps.bounds = new geom.Rect(0, 0, new geom.Size(this.labelWidth, labelProps.size.height));
        } else {
            labelProps.bounds = new geom.Rect(0, 0, labelProps.size);
            this.labelWidth = labelProps.size.width;
        }

        const iconsSize = new geom.Size(size.width - this.labelWidth, labelProps.size.height);
        const iconsProps = children.setProps(this.icons, iconsSize);
        iconsProps.bounds = new geom.Rect(this.labelWidth, 0, iconsProps.size);

        let totalHeight = iconsProps.size.height;
        if (this.showDescription) {
            const descriptionProps = children.setProps(this.description, new geom.Size(iconsSize.width + this.label.textBounds.width, size.height));
            descriptionProps.bounds = new geom.Rect(0, this.label.bounds.bottom, descriptionProps.size);
            totalHeight += descriptionProps.size.height;
        }

        return { size: new geom.Size(size.width, totalHeight) };
    }

    get buildAnimations() {
        const animations = [{
            target: this.DOMNode,
            type: AnimationSequenceType.FADE,
            duration: 400
        }, {
            type: AnimationSequenceType.ANIMATE_TO_VALUE,
            target: this,
            prepare: () => {
                this.animationState = this.icons.animationState = { value: 0 };
            },
            callback: value => {
                this.animationState = this.icons.animationState = { value };
            },
            reset: () => {
                this.animationState = this.icons.animationState = { value: 1 };
            },
            startValue: 0,
            endValue: 1,
        }];

        if (this.description) {
            animations.push({
                type: AnimationSequenceType.FADE,
                target: this.description.DOMNode,
                delay: duration => duration - 400
            });
        }

        return animations;
    }
}

export const elements = {
    PictorialCharts
};
